import { createContext, useContext, useLayoutEffect, useMemo, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useAccountStore } from '@/stores/accountStore';
import { useStoreSelectionStore } from '@/stores/storeSelectionStore';
import { getRouteTitle } from './pageTitles';

type RegisterTitle = (title: string) => () => void;
const PageTitleContext = createContext<RegisterTitle | null>(null);

export function PageTitleProvider({ children, userId }: { children: ReactNode; userId: string | null }) {
  const location = useLocation();
  const accountId = useAccountStore(state => state.currentAccountId);
  const storeId = useStoreSelectionStore(state => state.currentStoreId);
  // Identity, rather than a serialized key, also invalidates registrations when
  // back/forward navigation revisits a location seen earlier in this session.
  const scope = useMemo(() => ({ location, userId, accountId, storeId }), [location, userId, accountId, storeId]);
  const [override, setOverride] = useState<{ scope: object; owner: object; title: string } | null>(null);
  const register = useMemo<RegisterTitle>(() => title => {
    const owner = {};
    setOverride({ scope, owner, title });
    return () => setOverride(current => current?.owner === owner ? null : current);
  }, [scope]);
  const title = override?.scope === scope ? override.title : getRouteTitle(location.pathname);

  useLayoutEffect(() => {
    document.title = title;
  }, [title]);

  return <PageTitleContext.Provider value={register}>{children}</PageTitleContext.Provider>;
}

/** Only the active page registers a title; floating panels pass enabled=false. */
export function usePageTitle(title: string | null | undefined, enabled = true): void {
  const register = useContext(PageTitleContext);
  const normalized = title?.replace(/\s+/g, ' ').trim();
  useLayoutEffect(() => {
    if (enabled && normalized && register) return register(normalized);
  }, [enabled, normalized, register]);
}
